/**
 * 플레이리스트 관련 상수
 */

export const GENRES = [
  { value: 'pop', label: '팝' },
  { value: 'rock', label: '록' },
  { value: 'hip_hop', label: '힙합' },
  { value: 'jazz', label: '재즈' },
  { value: 'classical', label: '클래식' },
  { value: 'electronic', label: '일렉트로닉' },
  { value: 'indie', label: '인디' },
  { value: 'r_and_b', label: 'R&B' },
  { value: 'folk', label: '포크' },
  { value: 'metal', label: '메탈' },
] as const;

export const MOODS = [
  { value: 'happy', label: '행복한' },
  { value: 'sad', label: '슬픈' },
  { value: 'energetic', label: '에너지 넘치는' },
  { value: 'calm', label: '차분한' },
  { value: 'romantic', label: '로맨틱한' },
  { value: 'melancholic', label: '우울한' },
  { value: 'uplifting', label: '기분 좋은' },
  { value: 'intense', label: '강렬한' },
] as const;

export type Genre = typeof GENRES[number]['value'];
export type Mood = typeof MOODS[number]['value'];

/**
 * 참고곡 보컬 옵션
 */
export const VOCAL_OPTIONS = [
  { value: 'any', label: '상관없음' },
  { value: 'male', label: '남성' },
  { value: 'female', label: '여성' },
  { value: 'mixed', label: '혼성' },
] as const;

export type VocalOption = typeof VOCAL_OPTIONS[number]['value'];

/**
 * 참고곡 최대 개수
 */
export const MAX_REFERENCE_SONGS = 20;

/**
 * 플레이리스트 상태 상수
 * 백엔드 FSM과 동기화 필요
 */
export const PLAYLIST_STATUS = {
  PENDING: 'pending',
  GENERATING: 'generating',
  METADATA_READY: 'metadata_ready',
  MUSIC_GENERATING: 'music_generating',
  PAUSED: 'paused',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
  FAILED: 'failed',
  PUBLISHED: 'published',
} as const;

export type PlaylistStatusType = (typeof PLAYLIST_STATUS)[keyof typeof PLAYLIST_STATUS];

/**
 * 플레이리스트 상태별 표시 정보
 */
export const PLAYLIST_STATUS_INFO: Record<PlaylistStatusType, { label: string; color: string; description: string }> = {
  [PLAYLIST_STATUS.PENDING]: {
    label: '대기 중',
    color: 'gray',
    description: '플레이리스트 생성 대기 중입니다.',
  },
  [PLAYLIST_STATUS.GENERATING]: {
    label: '메타데이터 생성 중',
    color: 'blue',
    description: 'AI가 트랙 메타데이터를 생성하고 있습니다.',
  },
  [PLAYLIST_STATUS.METADATA_READY]: {
    label: '메타데이터 완료',
    color: 'yellow',
    description: '메타데이터 생성이 완료되었습니다. 검토 후 음악 생성을 시작하세요.',
  },
  [PLAYLIST_STATUS.MUSIC_GENERATING]: {
    label: '음악 생성 중',
    color: 'purple',
    description: 'AI가 음악을 생성하고 있습니다.',
  },
  [PLAYLIST_STATUS.PAUSED]: {
    label: '일시정지',
    color: 'orange',
    description: '생성이 일시정지되었습니다.',
  },
  [PLAYLIST_STATUS.CANCELLED]: {
    label: '취소됨',
    color: 'gray',
    description: '생성이 취소되었습니다.',
  },
  [PLAYLIST_STATUS.COMPLETED]: {
    label: '완료',
    color: 'green',
    description: '모든 트랙 생성이 완료되었습니다.',
  },
  [PLAYLIST_STATUS.FAILED]: {
    label: '실패',
    color: 'red',
    description: '생성 중 오류가 발생했습니다.',
  },
  [PLAYLIST_STATUS.PUBLISHED]: {
    label: '발행됨',
    color: 'emerald',
    description: 'YouTube에 발행되었습니다.',
  },
};

/**
 * 트랙 상태 상수
 */
export const TRACK_STATUS = {
  PENDING: 'pending',
  GENERATING: 'generating',
  METADATA_READY: 'metadata_ready',
  MUSIC_GENERATING: 'music_generating',
  PAUSED: 'paused',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
  FAILED: 'failed',
  PUBLISHED: 'published',
} as const;

export type TrackStatusType = (typeof TRACK_STATUS)[keyof typeof TRACK_STATUS];
